/**
 * SRS palette — scopes, commands, verbs, search provider for the global
 * command palette.
 *
 * Pattern mirrors notes/palette.ts:
 *   * scopes  — taxonomy under "Review" (the SRS section)
 *   * commands — palette-runnable actions
 *   * searchProviders — @srs <q> search across the card library
 *   * verbs — per-result actions surfaced on each card hit
 */
import { Archive, Brain, Clock, ClockCounterClockwise, SquaresFour, Pause, Play, Plus, Sparkle, Trash } from "@phosphor-icons/react";

import type {
  Command,
  FeaturePalette,
  PaletteCtx,
  Result,
  ScopeNode,
  SearchProvider,
  Verb,
} from "@/shared/components/command-palette/types";

import { listCards } from "./api/srs-api";
import { cardPrompt, type SRSCard } from "./types";

// ===================
// Scopes — taxonomy
// ===================

const SRS_PARENT: ScopeNode = {
  id: "srs",
  parentId: null,
  title: "Review",
};

const scopes: ScopeNode[] = [
  {
    id: "srs.library",
    parentId: "srs",
    title: "Card library",
    icon: SquaresFour,
    href: "/srs",
    keywords: ["all", "browse", "cards"],
  },
  {
    id: "srs.due",
    parentId: "srs",
    title: "Due now",
    icon: Sparkle,
    href: "/srs?state=due",
    keywords: ["queue", "review now"],
    resolveChildren: async () => {
      try {
        const page = await listCards({ state: "due", limit: 20 });
        return page.cards.map<ScopeNode>((card) => ({
          id: `srs.due.${card.id}`,
          parentId: "srs.due",
          title: cardPrompt(card),
          icon: Brain,
          href: `/srs/review?card=${card.id}`,
        }));
      } catch {
        return [];
      }
    },
  },
  {
    id: "srs.upcoming",
    parentId: "srs",
    title: "Upcoming",
    icon: Clock,
    href: "/srs?state=upcoming",
    keywords: ["scheduled", "later"],
  },
  {
    id: "srs.reviewed",
    parentId: "srs",
    title: "Recently reviewed",
    icon: ClockCounterClockwise,
    href: "/srs?state=reviewed",
    keywords: ["history", "done"],
  },
  {
    id: "srs.suspended",
    parentId: "srs",
    title: "Suspended",
    icon: Archive,
    href: "/srs?state=suspended",
    keywords: ["paused", "off-queue"],
  },
];

// ===================
// Verbs — per-card actions
// ===================

const cardVerbs: Verb[] = [
  {
    id: "srs.review-card",
    title: "Review this card",
    icon: Play,
    run: (entity, ctx) => {
      const id = (entity as { id: string }).id;
      ctx.router.push(`/srs/review?card=${id}`);
    },
  },
  {
    id: "srs.open-card",
    title: "Open card detail",
    icon: SquaresFour,
    run: (entity, ctx) => {
      const id = (entity as { id: string }).id;
      ctx.router.push(`/srs/cards/${id}`);
    },
  },
  {
    id: "srs.open-note",
    title: "Open in /notes",
    icon: SquaresFour,
    run: (entity, ctx) => {
      const id = (entity as { id: string }).id;
      ctx.router.push(`/notes/${id}`);
    },
  },
  {
    id: "srs.suspend",
    title: "Suspend",
    icon: Pause,
    confirm: {
      title: "Suspend this card?",
      body: "It will be removed from the review queue until you unsuspend.",
      cta: "Suspend",
    },
    run: () => {
      // The palette ConfirmPage calls the verb; the actual mutation runs
      // on the page that owns react-query context. Same shape as notes.
    },
  },
  {
    id: "srs.delete",
    title: "Delete card",
    icon: Trash,
    destructive: true,
    confirm: {
      title: "Delete this card?",
      body: "The underlying note is also deleted. This is permanent.",
      cta: "Delete",
    },
    run: () => {
      // Mutation runs on the page with react-query context.
    },
  },
];

// ===================
// Search provider — @srs <q>
// ===================

const cardsProvider: SearchProvider = {
  id: "srs",
  label: "Flashcards",
  icon: Brain,
  prefix: "@srs",
  async search(query, signal) {
    if (!query) return [];
    try {
      const page = await listCards({
        search: query,
        state: "all",
        limit: 8,
      });
      if (signal.aborted) return [];
      return page.cards.map<Result>((card) => buildCardResult(card));
    } catch {
      return [];
    }
  },
};

function buildCardResult(card: SRSCard): Result {
  const title = cardPrompt(card);
  return {
    id: `srs:${card.id}`,
    title,
    subtitle: card.state,
    icon: Brain,
    scopeChain: [SRS_PARENT],
    score: 50,
    entity: card,
    verbs: cardVerbs,
    onSelect: (ctx: PaletteCtx) =>
      ctx.push({
        kind: "entity",
        result: {
          id: `srs:${card.id}`,
          title,
          icon: Brain,
          scopeChain: [SRS_PARENT],
          onSelect: () => {},
          verbs: cardVerbs,
          entity: card,
        },
      }),
  };
}

// ===================
// Commands
// ===================

const commands: Command[] = [
  {
    id: "srs.start-review",
    title: "Start review session",
    icon: Play,
    scopeId: "srs",
    keywords: ["review", "queue", "study"],
    run: (ctx) => ctx.router.push("/srs/review"),
  },
  {
    id: "srs.browse-library",
    title: "Browse card library",
    icon: SquaresFour,
    scopeId: "srs",
    keywords: ["library", "grid", "all cards"],
    run: (ctx) => ctx.router.push("/srs"),
  },
  {
    id: "srs.create-card",
    title: "New flashcard",
    icon: Plus,
    scopeId: "srs",
    keywords: ["new", "card", "create", "flashcard"],
    run: (ctx) => ctx.router.push("/srs?new=1"),
  },
];

export const srsPalette: FeaturePalette = {
  scopes,
  commands,
  searchProviders: [cardsProvider],
};
